import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {Icons} from '../../assets';
import {DashboardLayout} from '../../components';
import {Button, ButtonSize, ButtonVariant} from '../../components/button';
import {ROUTES} from '../../constants/routes';
import {AccountContext} from '../../contexts';
import {useAuth} from '../../contexts/auth.provider';
import {useOptionalEmployerCandidate} from '../../contexts/employer-candidate.provider';
import {useOpportunity} from '../../contexts/opportunity.provider';
import {Account, AccountAuthDetails} from '../../models';
import {JobApplicationStatus} from '../../models/job-application';
import {Opportunity, OpportunityStatus} from '../../models/opportunity';
import {ListSavedCandidatesParams, OpportunitySearchParams, SaveCandidateParams} from '../../services';
import {OpportunitySortBy} from '../../services/opportunity';
import {ScreenState} from '../../types';
import {formatPhoneNum} from '../../utils';
import {ApplicantStatusControls} from '../job-applicant/components/applicant-status-controls';
import {ContactItem, ProfileHeaderSection} from './components/profile-header.component';
import {PublicProfileSkeleton} from './components/public-profile-skeleton.component';
import {ProfileSections} from './components/profile-sections.component';
import {PublicProfileOtpGate} from './components/otp-gate.component';
import {PublicShell} from './components/public-shell.component';

enum PublicProfileViewMode {
  DASHBOARD = 'dashboard',
  OTP = 'otp',
  PUBLIC = 'public',
}

enum PublicProfileSource {
  APPLIED = 'applied',
  RECOMMENDED = 'recommended',
  SEARCH = 'search',
}

const PublicProfilePage: React.FC = () => {
  const {accountId} = useParams<{accountId: string}>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source');
  const source =
    sourceParam === PublicProfileSource.APPLIED ||
    sourceParam === PublicProfileSource.RECOMMENDED ||
    sourceParam === PublicProfileSource.SEARCH
      ? sourceParam
      : null;
  const candidateIdFromState = (location.state as {candidateId?: string} | null)?.candidateId;
  const candidateIdParam = searchParams.get('candidateId');
  const candidateId = candidateIdFromState || candidateIdParam || '';
  const applicationId = searchParams.get('applicationId') || '';
  const opportunityId = searchParams.get('opportunityId') || '';
  const isFromSearch = source === PublicProfileSource.SEARCH || searchParams.get('fromSearch') === 'true';
  const isFromRecommendedOpportunity = source === PublicProfileSource.RECOMMENDED && Boolean(opportunityId);
  const isFromAppliedOpportunity = source === PublicProfileSource.APPLIED && Boolean(opportunityId && applicationId);
  const {accountService} = useContext(AccountContext);
  const {isAuthenticated, login, logout, organizationInfo, user} = useAuth();
  const {getAllOpportunities, inviteCandidate, listInvitations, updateApplicationStatus} = useOpportunity();
  const [account, setAccount] = useState<Account>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [otpAccount, setOtpAccount] = useState<Account | null>(null);
  const [otpState, setOtpState] = useState<ScreenState>(ScreenState.DEFAULT);
  const [otpError, setOtpError] = useState('');

  const employerCandidateMethods = useOptionalEmployerCandidate();

  const canManageCandidates = Boolean(isAuthenticated && employerCandidateMethods);

  const listSavedCandidates = canManageCandidates
    ? employerCandidateMethods!.listSavedCandidates
    : async () => ({data: [], limit: 0, page: 1, total: 0, totalPages: 1});
  const removeSavedCandidate = canManageCandidates ? employerCandidateMethods!.removeSavedCandidate : async () => {};
  const saveCandidate = canManageCandidates
    ? employerCandidateMethods!.saveCandidate
    : async () => {
        throw new Error('Please sign in to save candidates');
      };

  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedCandidateId, setSavedCandidateId] = useState<string | null>(null);
  const [checkingSaved, setCheckingSaved] = useState(false);
  const [availableOpportunities, setAvailableOpportunities] = useState<Opportunity[]>([]);
  const [inviteError, setInviteError] = useState('');
  const [inviteStatusByOpportunityId, setInviteStatusByOpportunityId] = useState<Record<string, boolean>>({});
  const [inviting, setInviting] = useState(false);
  const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);
  const [showOpportunitySelector, setShowOpportunitySelector] = useState(false);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState<JobApplicationStatus | null>(null);
  const [applicationStatus, setApplicationStatus] = useState<JobApplicationStatus>(() => {
    const status = searchParams.get('status');
    if (status === JobApplicationStatus.ACCEPTED || status === JobApplicationStatus.REJECTED || status === JobApplicationStatus.APPLIED) {
      return status;
    }
    return JobApplicationStatus.APPLIED;
  });
  const hasCandidateContext = Boolean(candidateId) && canManageCandidates;
  const isPublicView = isAuthenticated && !organizationInfo;
  const shouldShowSaveAction = Boolean((isFromSearch || isFromRecommendedOpportunity) && hasCandidateContext && !isPublicView);
  const shouldHidePhoneNumber = isFromSearch || isFromRecommendedOpportunity;

  const handleOtpVerified = ({account: verifiedAccount, auth}: {account: Account; auth: AccountAuthDetails}) => {
    if (!auth.token) {
      setOtpError('We could not verify your session. Please request a new OTP.');
      setOtpState(ScreenState.ERROR);
      return;
    }

    login(auth.token, {
      id: verifiedAccount.id,
      firstName: verifiedAccount.firstName,
      lastName: verifiedAccount.lastName,
      email: verifiedAccount.email,
    });
    setLoading(true);
    setReloadKey(prev => prev + 1);
  };

  const handlePublicLogout = () => {
    logout();
    setAccount(undefined);
    setIsSaved(false);
    setSavedCandidateId(null);
    setOtpAccount(null);
    setOtpError('');
    setOtpState(ScreenState.DEFAULT);
    setLoading(false);
  };

  const handleSaveCandidate = async () => {
    if (!hasCandidateContext) {
      return;
    }
    try {
      setIsSaving(true);
      if (isSaved && savedCandidateId) {
        await removeSavedCandidate(savedCandidateId);
        setIsSaved(false);
        setSavedCandidateId(null);
        return;
      }
      const savedCandidate = await saveCandidate(new SaveCandidateParams({candidateProfileId: candidateId}));
      setIsSaved(true);
      setSavedCandidateId(savedCandidate.id);
    } catch (err) {
      console.error('Unable to update saved candidate', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadOpenOpportunities = async () => {
    if (availableOpportunities.length > 0) {
      return;
    }
    try {
      setIsLoadingOpportunities(true);
      const response = await getAllOpportunities(
        new OpportunitySearchParams({
          limit: 50,
          page: 1,
          sortBy: OpportunitySortBy.NEWEST,
          status: OpportunityStatus.PUBLISHED,
        }),
      );
      setAvailableOpportunities(response.data);
      if (!selectedOpportunityId && response.data[0]?.id) {
        setSelectedOpportunityId(response.data[0].id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load open opportunities. Please try again.';
      setInviteError(message);
    } finally {
      setIsLoadingOpportunities(false);
    }
  };

  const handleInvite = async (targetOpportunityId?: string) => {
    if (!account?.id) {
      return;
    }
    const resolvedOpportunityId = targetOpportunityId || selectedOpportunityId;
    if (!resolvedOpportunityId) {
      setInviteError('Please choose an open opportunity before sending an invite.');
      return;
    }
    try {
      setInviteError('');
      setInviting(true);
      await inviteCandidate(resolvedOpportunityId, account.id);
      setInviteStatusByOpportunityId(prev => ({...prev, [resolvedOpportunityId]: true}));
      toast.success('Invitation sent');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to send invitation right now. Please try again.';
      setInviteError(message);
      toast.error(message);
    } finally {
      setInviting(false);
    }
  };

  const loadInviteStatus = async (targetOpportunityId: string) => {
    if (!targetOpportunityId || !account?.id || !user?.id) {
      return;
    }
    try {
      const invitations = await listInvitations(targetOpportunityId, account.id);
      const hasInvite = invitations.some(invitation => String(invitation.status || '').toUpperCase() === JobApplicationStatus.INVITED);
      setInviteStatusByOpportunityId(prev => ({...prev, [targetOpportunityId]: hasInvite}));
    } catch (err) {
      console.error('Unable to load invitation status', err);
    }
  };

  const handleStatusChange = async (newStatus: JobApplicationStatus) => {
    if (!isFromAppliedOpportunity || !opportunityId || !applicationId) {
      return;
    }
    try {
      setUpdatingStatus(newStatus);
      await updateApplicationStatus(opportunityId, applicationId, newStatus);
      setApplicationStatus(newStatus);
      toast.success(newStatus === JobApplicationStatus.ACCEPTED ? 'Candidate shortlisted' : 'Candidate rejected');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to update candidate status. Please try again.';
      toast.error(message);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const phoneLabel = useMemo(() => {
    if (!account?.phoneNumber) {
      return 'Not available';
    }
    return formatPhoneNum(account.phoneNumber.countryCode, account.phoneNumber.phoneNumber);
  }, [account?.phoneNumber]);

  useEffect(() => {
    let isMounted = true;

    const fetchAccount = async () => {
      const identifier = accountId?.trim();
      if (!identifier) {
        setError('Missing profile identifier. Please check the link or request a fresh invite.');
        setLoading(false);
        return;
      }

      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await accountService.getAccount(identifier);

        if (!isMounted) {
          return;
        }

        if (response.data) {
          setAccount(response.data);
        } else {
          setError('We could not find this Aura. It may be private or no longer available.');
        }
      } catch (err) {
        if (isMounted) {
          setError('We were unable to load this profile. Please refresh or try again later.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAccount();
    return () => {
      isMounted = false;
    };
  }, [accountId, accountService, reloadKey, isAuthenticated]);

  useEffect(() => {
    const checkIfSaved = async () => {
      if (!hasCandidateContext || !isAuthenticated) {
        setIsSaved(false);
        setSavedCandidateId(null);
        return;
      }
      try {
        setCheckingSaved(true);
        const response = await listSavedCandidates(new ListSavedCandidatesParams({limit: 100, page: 1}));
        const savedCandidate = response.data.find(saved => saved.candidateProfile?.id === candidateId);
        if (savedCandidate) {
          setIsSaved(true);
          setSavedCandidateId(savedCandidate.id);
        } else {
          setIsSaved(false);
          setSavedCandidateId(null);
        }
      } catch (err) {
        console.error('Unable to check saved candidate status', err);
        setIsSaved(false);
        setSavedCandidateId(null);
      } finally {
        setCheckingSaved(false);
      }
    };

    if (hasCandidateContext) {
      checkIfSaved();
    }
  }, [candidateId, hasCandidateContext, listSavedCandidates]);

  useEffect(() => {
    if (!isFromRecommendedOpportunity || !opportunityId || !account?.id || !user?.id) {
      return;
    }
    loadInviteStatus(opportunityId);
  }, [account?.id, isFromRecommendedOpportunity, opportunityId, user?.id]);

  useEffect(() => {
    if (!isFromSearch || !showOpportunitySelector || !selectedOpportunityId || !account?.id || !user?.id) {
      return;
    }
    loadInviteStatus(selectedOpportunityId);
  }, [account?.id, isFromSearch, selectedOpportunityId, showOpportunitySelector, user?.id]);

  const handleRetry = () => {
    setAccount(undefined);
    setError('');
    setReloadKey(prev => prev + 1);
  };

  const contactItems = useMemo<ContactItem[]>(() => {
    if (!account) {
      return [];
    }

    const items: ContactItem[] = [];

    if (account.email) {
      items.push({
        href: `mailto:${account.email}`,
        icon: Icons.EMAIL,
        label: account.email,
      });
    }

    if (account.phoneNumber && !shouldHidePhoneNumber) {
      items.push({
        icon: Icons.PHONE,
        label: phoneLabel,
      });
    }

    if (account.linkedInProfileUrl) {
      items.push({
        href: account.linkedInProfileUrl,
        icon: Icons.LINK_ICON,
        label: 'LinkedIn',
        newTab: true,
      });
    }

    if (account.instagramProfileUrl) {
      items.push({
        href: account.instagramProfileUrl,
        icon: Icons.LINK_ICON,
        label: 'Instagram',
        newTab: true,
      });
    }

    if (account.facebookProfileUrl) {
      items.push({
        href: account.facebookProfileUrl,
        icon: Icons.LINK_ICON,
        label: 'Facebook',
        newTab: true,
      });
    }

    return items;
  }, [account, phoneLabel, shouldHidePhoneNumber]);

  const displayName = account?.displayNameOrPhone || 'Aura Member';
  const headerBreadcrumbs = useMemo(() => {
    const breadcrumbs: {label: string; path?: string}[] = [{label: 'Dashboard', path: ROUTES.DASHBOARD}];

    if (isFromAppliedOpportunity) {
      breadcrumbs.push({label: 'Opportunities', path: ROUTES.OPPORTUNITIES});
      if (opportunityId) {
        breadcrumbs.push({label: 'Applicants', path: `/opportunities/${opportunityId}/applicants`});
      }
    } else if (isFromRecommendedOpportunity) {
      breadcrumbs.push({label: 'Opportunities', path: ROUTES.OPPORTUNITIES});
      if (opportunityId) {
        breadcrumbs.push({label: 'Recommended', path: `/opportunities/${opportunityId}`});
      }
    } else if (isFromSearch) {
      breadcrumbs.push({label: 'Search Candidates', path: ROUTES.SEARCH_CANDIDATES});
    }

    breadcrumbs.push({label: displayName});
    return breadcrumbs;
  }, [displayName, isFromAppliedOpportunity, isFromRecommendedOpportunity, isFromSearch, opportunityId]);

  const renderOtpGate = () => (
    <PublicProfileOtpGate
      account={otpAccount}
      errorMessage={otpError}
      onAccountChange={setOtpAccount}
      onErrorChange={setOtpError}
      onStateChange={setOtpState}
      onVerified={handleOtpVerified}
      screenState={otpState}
    />
  );

  const renderPublicLayout = (content: React.ReactNode) => <PublicShell onLogout={handlePublicLogout}>{content}</PublicShell>;

  const savedCandidatesHeaderAction = shouldShowSaveAction ? (
    <Button
      className="rounded-full px-3 py-2 text-gray-800"
      onClick={() => navigate(ROUTES.SAVED_CANDIDATES)}
      size={ButtonSize.SM}
      startIcon={<img alt="Saved Candidates" className="h-6 w-6" src={Icons.BOOKMARK_FILLED} />}
      variant={ButtonVariant.Secondary}>
      Saved Candidates
    </Button>
  ) : null;

  const renderDashboardLayout = (content: React.ReactNode) => (
    <DashboardLayout
      breadcrumbs={headerBreadcrumbs}
      className="bg-white"
      contentPaddingClassName="px-8 pb-10 pt-6"
      headerActions={savedCandidatesHeaderAction}>
      {content}
    </DashboardLayout>
  );

  const inviteAction = useMemo(() => {
    if (isPublicView || !account?.id) {
      return null;
    }

    if (isFromRecommendedOpportunity) {
      const isInvitedOnOpportunity = Boolean(inviteStatusByOpportunityId[opportunityId]);
      return (
        <div className="flex flex-col gap-2">
          {inviteError && <p className="text-xs text-red-600">{inviteError}</p>}
          <Button
            disabled={inviting || isInvitedOnOpportunity}
            loading={inviting}
            onClick={() => handleInvite(opportunityId)}
            size={ButtonSize.SM}
            variant={isInvitedOnOpportunity ? ButtonVariant.Secondary : ButtonVariant.Primary}>
            {isInvitedOnOpportunity ? 'Invited' : 'Invite'}
          </Button>
        </div>
      );
    }

    if (!isFromSearch) {
      return null;
    }

    const confirmInvite = async () => {
      await handleInvite();
    };
    const isInvitedOnSelectedOpportunity = Boolean(selectedOpportunityId && inviteStatusByOpportunityId[selectedOpportunityId]);

    return (
      <div className="flex flex-col gap-2">
        {showOpportunitySelector && (
          <select
            className="min-w-[240px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
            disabled={inviting || isLoadingOpportunities}
            onChange={event => setSelectedOpportunityId(event.target.value)}
            value={selectedOpportunityId}>
            <option value="">Select an opportunity</option>
            {availableOpportunities.map(opportunity => (
              <option key={opportunity.id} value={opportunity.id}>
                {opportunity.jobTitle}
              </option>
            ))}
          </select>
        )}
        {inviteError && <p className="text-xs text-red-600">{inviteError}</p>}
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={inviting || isInvitedOnSelectedOpportunity}
            loading={inviting}
            onClick={async () => {
              if (!showOpportunitySelector) {
                setShowOpportunitySelector(true);
                await handleLoadOpenOpportunities();
                return;
              }
              await confirmInvite();
            }}
            size={ButtonSize.SM}
            variant={isInvitedOnSelectedOpportunity ? ButtonVariant.Secondary : ButtonVariant.Primary}>
            {isInvitedOnSelectedOpportunity ? 'Invited' : showOpportunitySelector ? 'Send Invite' : 'Invite'}
          </Button>
          {showOpportunitySelector && (
            <Button
              disabled={inviting || isLoadingOpportunities}
              onClick={() => setShowOpportunitySelector(false)}
              size={ButtonSize.SM}
              variant={ButtonVariant.Outline}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    );
  }, [
    account?.id,
    availableOpportunities,
    inviteError,
    inviteStatusByOpportunityId,
    inviting,
    isFromRecommendedOpportunity,
    isFromSearch,
    isLoadingOpportunities,
    isPublicView,
    opportunityId,
    selectedOpportunityId,
    showOpportunitySelector,
  ]);

  const statusAction = useMemo(() => {
    if (isPublicView || !isFromAppliedOpportunity) {
      return null;
    }
    return <ApplicantStatusControls loadingStatus={updatingStatus} onChange={handleStatusChange} status={applicationStatus} />;
  }, [applicationStatus, handleStatusChange, isFromAppliedOpportunity, isPublicView, updatingStatus]);

  const profileActions = useMemo(() => {
    if (statusAction) {
      return statusAction;
    }
    return inviteAction;
  }, [inviteAction, statusAction]);

  const renderProfileContent = () => {
    if (loading) {
      return <PublicProfileSkeleton />;
    }

    if (error) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 text-center">
          <div className="max-w-lg space-y-4">
            <h1 className="text-2xl font-semibold text-gray-900">Unable to load profile</h1>
            <p className="text-base text-gray-600">{error}</p>
          </div>
          <Button onClick={handleRetry} size={ButtonSize.SM} variant={ButtonVariant.Primary}>
            Try again
          </Button>
        </div>
      );
    }

    if (!account) {
      return null;
    }

    return (
      <>
        <ProfileHeaderSection
          account={account}
          checkingSaved={checkingSaved}
          contactItems={contactItems}
          displayName={displayName}
          extraActions={profileActions}
          isSaved={isSaved}
          isSaving={isSaving}
          onSaveToggle={handleSaveCandidate}
          showSaveAction={shouldShowSaveAction}
        />
        <ProfileSections account={account} />
      </>
    );
  };

  const viewMode: PublicProfileViewMode = !isAuthenticated
    ? PublicProfileViewMode.OTP
    : isPublicView
    ? PublicProfileViewMode.PUBLIC
    : PublicProfileViewMode.DASHBOARD;
  let resolvedPage: React.ReactNode;

  if (viewMode === PublicProfileViewMode.OTP) {
    resolvedPage = renderOtpGate();
  } else {
    const profileContent = renderProfileContent();
    resolvedPage = viewMode === PublicProfileViewMode.PUBLIC ? renderPublicLayout(profileContent) : renderDashboardLayout(profileContent);
  }

  return resolvedPage;
};

export default PublicProfilePage;
